import React from "react";
import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";

/**
 * What to send: one card per option, laid out as a radiogroup.
 *
 * Cards rather than the pill toggles used elsewhere in the form, because each
 * option needs a line explaining what the borrower actually receives — "Both"
 * being one email with two links is not something a two-word pill can carry, and
 * it is exactly the thing a broker needs to know before choosing.
 *
 * Reused for the delivery choice below it, which is why `ariaLabel` and the
 * optional per-option `icon` exist: two radiogroups on one screen need distinct
 * accessible names, and email-vs-link is a distinction a glyph carries faster
 * than the label does.
 *
 * `columns` defaults to one track per option, so a group fills its row. Pass it
 * explicitly to hold a SHORTER group to the same track width as a longer one
 * stacked above it — two cards in three columns line up with the three above
 * them, where two half-width cards would not.
 */
const SendKindSelector = ({ options, value, onChange, ariaLabel, columns }) => (
  <Box
    role="radiogroup"
    aria-label={ariaLabel || "What are you sending?"}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: `repeat(${columns || options.length}, 1fr)`,
      },
      gap: 1.25,
    }}
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <ButtonBase
          key={option.value}
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          sx={(theme) => ({
            display: "block",
            textAlign: "left",
            p: 1.75,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: selected ? theme.palette.primary.main : colors.slate300,
            backgroundColor: selected
              ? alpha(theme.palette.primary.main, 0.06)
              : "#fff",
            // Inset ring rather than a 2px border: a thicker border on select
            // would reflow the card and nudge its neighbours.
            boxShadow: selected
              ? `inset 0 0 0 1px ${theme.palette.primary.main}`
              : "none",
            transition: "border-color .15s, background-color .15s",
            "&:hover": { borderColor: theme.palette.primary.main },
          })}
        >
          <Flx centerY gap={1}>
            {/* A real radio, not a filled disc: the inset white ring leaves a
                brand-colored centre dot, which is what reads as "selected"
                rather than "highlighted". */}
            <Box
              sx={(theme) => ({
                flex: "none",
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "1.5px solid",
                borderColor: selected
                  ? theme.palette.primary.main
                  : colors.slate300,
                backgroundColor: selected
                  ? theme.palette.primary.main
                  : "#fff",
                boxShadow: selected ? "inset 0 0 0 3px #fff" : "none",
                transition: "all .15s",
              })}
            />
            {option.icon && (
              <option.icon
                sx={(theme) => ({
                  flex: "none",
                  fontSize: "17px !important",
                  color: `${
                    selected ? theme.palette.primary.main : colors.slate400
                  } !important`,
                })}
              />
            )}
            <Txt
              component="div"
              sx={{ fontSize: 13.5, fontWeight: 700, color: colors.slate900 }}
            >
              {option.label}
            </Txt>
          </Flx>
          <Txt
            component="div"
            sx={{
              fontSize: 12,
              color: colors.slate600,
              mt: 0.5,
              // Aligns the description under the label, clearing the radio and
              // the icon when there is one.
              pl: option.icon ? 4.25 : 3,
            }}
          >
            {option.description}
          </Txt>
        </ButtonBase>
      );
    })}
  </Box>
);

export default SendKindSelector;
